import { computed } from 'vue';
import { required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';

/**
 * @function useIncrementalDataField
 * @description A composable that provides a way to validate and submit an incremental data field.
 * @param {string} fieldName The name of the input field.
 * @param {object} fields A raw reactive object containing the field model values.
 * @param {object} serverPayload A computed property containing server payload requirements.
 * @param {function} emit Emit function from the consuming component. If not passed emitted events will have no component context.
 * @returns {object} An object with the following properties:
 *   - v$: Vuelidate instance setup with rules.
 *   - submit: Function to submit the incremental data field.
 */
export function useIncrementalDataField(fieldName, fields, serverPayload, emit) {
  const validations = computed(() => ({
    fields: {
      [fieldName]: { required },
    },
  }));

  const v$ = useVuelidate(validations, { fields });

  const submit = async () => {
    if (v$.value) {
      const valid = await v$.value.$validate();

      if (!valid) {
        emit('incremental-data-field:validation-error');
        return;
      }
    }

    emit('incremental-data-field:submit', {
      payload: serverPayload.value,
      fieldName,
    });
  };

  return {
    v$,
    submit,
  };
}
